// src/components/ResponsiveDataTable.vue

<template>
  <div ref="tableContainer">
    <v-data-table v-model:expanded="expandedRows" :headers="dynamicHeaders" :items="items"
      :item-value="item => getProperty(item, uniqueItemId)" v-bind="$attrs">
      <template v-slot:item.expandAction="{ item }">
        <v-btn
          icon
          variant="text"
          size="small"
          :disabled="!hiddenHeaders.length && !getGroupItems(item).length"
          @click="toggleExpand(item)"
        >
          <v-icon>{{ isExpanded(item) ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
        </v-btn>
      </template>

      <template v-slot:expanded-row="{ columns, item }">
        <tr v-if="isExpanded(item)">
          <td :colspan="columns.length">
            <v-card class="ma-2" density="compact">
              <v-card-text>
                <v-row v-for="header in hiddenHeaders" :key="header.key" class="hidden-row-item">
                  <v-col cols="6" md="4">
                    <strong>{{ header.title }}:</strong>
                  </v-col>
                  <v-col cols="6" md="8">
                    <slot :name="`item.${header.key}`" :item="item" :value="getValue(item, header)">
                      <span>{{ getValue(item, header) }}</span>
                    </slot>
                  </v-col>
                </v-row>

                <template v-if="groupKey && getGroupItems(item).length">
                  <v-divider v-if="hiddenHeaders.length" class="my-2" />
                  <div v-for="(groupItem, index) in getGroupItems(item)" :key="index" class="group-row-item">
                    <slot name="group-item" :item="item" :group-item="groupItem" :index="index">
                      <span>{{ groupItem }}</span>
                    </slot>
                  </div>
                </template>
              </v-card-text>
            </v-card>
          </td>
        </tr>
      </template>


      <!-- Slots como no-data são chamados pelo v-data-table sem argumentos: slotData vem undefined. -->
      <template v-for="(_, name) in $slots" v-slot:[name]="slotData">
        <slot :name="name" v-bind="slotData || {}" />
      </template>
    </v-data-table>
  </div>
</template>

<style scoped>
.hidden-row-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 8px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}

.hidden-row-item:last-child {
  border-bottom: none;
}

.hidden-row-item strong {
  margin-right: 8px;
}

.group-row-item {
  padding: 6px 8px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}

.group-row-item:last-child {
  border-bottom: none;
}
</style>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue';

const props = defineProps({
  headers: { type: Array, required: true },
  items: { type: Array, required: true },
  uniqueItemId: { type: String, default: 'id' },
  // Nome da propriedade do item que contém os "demais atendimentos do grupo"
  // (array). Quando informado, cada linha ganha um botão de expansão para
  // listá-los, mesmo que nenhuma coluna esteja escondida responsivamente.
  groupKey: { type: String, default: null }
});

const tableContainer = ref(null);
const containerWidth = ref(0);
const expandedRows = ref([]);

// --- MUDANÇA PRINCIPAL AQUI ---
// Função getProperty agora é robusta e lida com as duas estruturas de 'item'
const getProperty = (item, path) => {
  // Usa item.raw se existir, senão, usa o próprio item.
  const source = item.raw || item;
  if (!source) return undefined;
  return path.split('.').reduce((acc, part) => acc && acc[part], source);
};

const getValue = (item, header) => {
  const source = item.raw || item;
  if (!source) return undefined;

  if (typeof header.value === "function") {
    return header.value(source);
  }

  return header.key.split('.').reduce((acc, part) => acc && acc[part], source);
};


const processedHeaders = computed(() => {
  const allHeaders = [...props.headers];
  const expanderColumnWidth = 60;
  const visible = [];
  const hidden = [];

  let tempWidth = 0;
  const headersIfNoButton = [];
  for (const header of allHeaders) {
    const headerWidth = header.minWidth || 150;
    if (tempWidth + headerWidth <= containerWidth.value) {
      headersIfNoButton.push(header);
      tempWidth += headerWidth;
    }
  }

  const isButtonNeeded = headersIfNoButton.length < allHeaders.length;

  let finalWidth = 0;
  const availableWidth = containerWidth.value - (isButtonNeeded ? expanderColumnWidth : 0);

  for (const header of allHeaders) {
    const headerWidth = header.minWidth || 150;
    if (finalWidth + headerWidth < availableWidth) {
      visible.push(header);
      finalWidth += headerWidth;
    } else {
      hidden.push(header);
    }
  }

  return { visible, hidden };
});

const visibleHeaders = computed(() => processedHeaders.value.visible);
const hiddenHeaders = computed(() => processedHeaders.value.hidden);
const hasHiddenColumns = computed(() => hiddenHeaders.value.length > 0);

const getGroupItems = (item) => {
  if (!props.groupKey) return [];
  const source = item.raw || item;
  return (source && source[props.groupKey]) || [];
};

const hasExpandableGroups = computed(() => {
  if (!props.groupKey) return false;
  return props.items.some((item) => getGroupItems(item).length > 0);
});

const showExpandColumn = computed(() => hasHiddenColumns.value || hasExpandableGroups.value);

watch(showExpandColumn, (isNowShown) => {
  if (!isNowShown) {
    expandedRows.value = [];
  }
});

const dynamicHeaders = computed(() => {
  if (showExpandColumn.value) {
    return [
      { key: 'expandAction', title: '', sortable: false, width: '60px', align: 'center' },
      ...visibleHeaders.value
    ];
  }
  return visibleHeaders.value;
});

// As chamadas para getProperty agora passam 'item' diretamente, sem '.raw'
const toggleExpand = (item) => {
  const itemId = getProperty(item, props.uniqueItemId);
  // console.log(itemId, props.uniqueItemId);

  const index = expandedRows.value.indexOf(itemId);
  if (index > -1) {
    expandedRows.value.splice(index, 1);
  } else {
    expandedRows.value.push(itemId);
  }
};

const isExpanded = (item) => {
  const itemId = getProperty(item, props.uniqueItemId);
  return expandedRows.value.includes(itemId);
};


let resizeObserver;
onMounted(() => {
  if (tableContainer.value) {
    resizeObserver = new ResizeObserver(entries => {
      if (entries[0]) {
        containerWidth.value = entries[0].contentRect.width;
      }
    });
    resizeObserver.observe(tableContainer.value);
  }
});

onBeforeUnmount(() => {
  if (resizeObserver && tableContainer.value) {
    resizeObserver.unobserve(tableContainer.value);
  }
});
</script>

<style scoped>
:deep(.v-table__wrapper) {
  overflow-x: hidden !important;
}
</style>